import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconCheckCircle, IconClose, IconEdit, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { formatDateOnly } from '../../../common/utils/date';
import { fmtMoney, digitsOnly, groupThousands } from '../../../common/utils/money';
import ClientFarmSearch from '../../clients/forms/search';
import { useAnimalOwners, useAddOwner, useUpdateOwner, useDeleteOwner } from '../contexts/ownerships';

import './styles/owners-modal.css';

const today = () => new Date().toLocaleDateString('en-CA');

// Input de dinero amigable: prefijo "$", miles con separador; guarda solo dígitos.
function MoneyInput({ value, onChange, disabled, placeholder }) {
    return (
        <span className="own-money">
            <span className="own-money__sign">$</span>
            <input
                type="text"
                inputMode="numeric"
                value={groupThousands(value)}
                placeholder={placeholder}
                onChange={(e) => onChange(digitsOnly(e.target.value))}
                disabled={disabled}
            />
        </span>
    );
}

// Copropietarios (cowhousing, admin-animal-ownerships.md · 0043/0047): asocia clientes a
// una donadora en % con fecha y valor de compra. Invariante Σ ≤ 100 (resto = planta),
// validado en cliente (contra `planta_percent`) y server (422). El valor de la vaca
// (`gross_value`) y la participación por owner (`stake_value`) los deriva el backend.
export default function OwnersModal({ animal, onClose }) {
    const animalId = animal.id;
    const { data, isLoading, error } = useAnimalOwners(animalId);
    const { mutateAsync: add, isPending: adding } = useAddOwner(animalId);
    const { mutateAsync: update, isPending: updating } = useUpdateOwner(animalId);
    const { mutateAsync: remove, isPending: removing } = useDeleteOwner(animalId);
    const busy = adding || updating || removing;
    const cardRef = useModalDialog(onClose);

    const owners = data?.items || [];
    const total = data?.total_percent ?? owners.reduce((a, o) => a + (Number(o.percent) || 0), 0);
    const planta = data?.planta_percent ?? Math.max(0, 100 - total);
    const grossValue = data?.gross_value;

    // alta
    const [client, setClient] = useState(null);
    const [pct, setPct] = useState('');
    const [pdate, setPdate] = useState('');
    const [pvalue, setPvalue] = useState('');
    // edición
    const [editId, setEditId] = useState(null);
    const [edit, setEdit] = useState({ percent: '', purchase_date: '', purchase_value: '' });

    const resetAdd = () => { setClient(null); setPct(''); setPdate(''); setPvalue(''); };

    const pick = (c) => {
        if (owners.some((o) => o.client?.id === c.id)) { toast.error('Ese cliente ya es copropietario (editá su registro)'); return; }
        setClient(c);
        if (!pdate) setPdate(today());
    };

    // arma el payload económico (solo campos con valor)
    const econ = (percent, date, value) => {
        const out = { percent: Number(percent) };
        if (date) out.purchase_date = date;
        if (value !== '' && value != null) out.purchase_value = Number(value);
        return out;
    };

    const addOwner = async () => {
        const n = Number(pct);
        if (!client) { toast.error('Elegí un cliente'); return; }
        if (!(n > 0)) { toast.error('El % debe ser mayor a 0'); return; }
        if (n > planta) { toast.error(`Máximo disponible: ${planta}%`); return; }
        try {
            await add({ client_id: client.id, ...econ(pct, pdate, pvalue) });
            toast.success('Copropietario agregado');
            resetAdd();
        } catch (er) {
            if (er?.status === 422) toast.error('La suma de porcentajes superaría 100%');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo agregar');
        }
    };

    const startEdit = (o) => {
        setEditId(o.id);
        setEdit({ percent: String(o.percent ?? ''), purchase_date: o.purchase_date || '', purchase_value: o.purchase_value != null ? digitsOnly(String(o.purchase_value)) : '' });
    };
    const saveEdit = async (o) => {
        const n = Number(edit.percent);
        const max = planta + (Number(o.percent) || 0); // liberar el % propio
        if (!(n > 0)) { toast.error('El % debe ser mayor a 0'); return; }
        if (n > max) { toast.error(`Máximo para este cliente: ${max}%`); return; }
        try {
            await update({ ownerId: o.id, ...econ(edit.percent, edit.purchase_date, edit.purchase_value) });
            toast.success('Copropietario actualizado');
            setEditId(null);
        } catch (er) {
            if (er?.status === 422) toast.error('La suma de porcentajes superaría 100%');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo actualizar');
        }
    };

    const del = async (o) => {
        try { await remove(o.id); toast.success('Copropietario quitado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo quitar'); }
    };

    const label = animal.name || animal.number || `#${animalId}`;

    return (
        <div className="own-modal" role="dialog" aria-modal="true" aria-label="Copropietarios">
            <div className="own-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="own-modal__card" ref={cardRef}>
                <header className="own-modal__head">
                    <div>
                        <h2 className="own-modal__title">Copropietarios</h2>
                        <span className="own-modal__sub">{label}</span>
                    </div>
                    <button type="button" className="own-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="own-bar">
                    <div className="own-bar__track"><span className="own-bar__fill" style={{ width: `${Math.min(100, total)}%` }} /></div>
                    <div className="own-bar__legend">
                        <span>Asignado <strong>{total}%</strong></span>
                        <span>Planta <strong>{planta}%</strong></span>
                    </div>
                    <p className="own-gross">Valor estimado de la vaca: <strong>{fmtMoney(grossValue)}</strong> <span className="own-gross__note">(derivado del último % comprado)</span></p>
                </div>

                {error ? (
                    <p className="own-modal__error">No se pudieron cargar los copropietarios.</p>
                ) : isLoading ? (
                    <p className="own-modal__state">Cargando…</p>
                ) : (
                    <ul className="own-list">
                        {owners.map((o) => (
                            <li key={o.id} className="own-item">
                                <div className="own-item__top">
                                    <span className="own-item__name">{o.client?.name || `Cliente #${o.client?.id}`}</span>
                                    {editId !== o.id && (
                                        <span className="own-item__tools">
                                            <button type="button" className="own-icon" onClick={() => startEdit(o)} disabled={busy} aria-label="Editar" title="Editar"><IconEdit /></button>
                                            <button type="button" className="own-icon own-icon--danger" onClick={() => del(o)} disabled={busy} aria-label="Quitar" title="Quitar"><IconTrash /></button>
                                        </span>
                                    )}
                                </div>

                                {editId === o.id ? (
                                    <div className="own-edit">
                                        <label className="own-field own-field--pct">
                                            <span className="own-field__label">%</span>
                                            <input type="number" min="1" max="100" value={edit.percent} onChange={(e) => setEdit((s) => ({ ...s, percent: e.target.value }))} disabled={busy} />
                                        </label>
                                        <label className="own-field">
                                            <span className="own-field__label">Fecha de compra</span>
                                            <input type="date" max={today()} value={edit.purchase_date} onChange={(e) => setEdit((s) => ({ ...s, purchase_date: e.target.value }))} disabled={busy} />
                                        </label>
                                        <label className="own-field">
                                            <span className="own-field__label">Valor de compra</span>
                                            <MoneyInput value={edit.purchase_value} onChange={(v) => setEdit((s) => ({ ...s, purchase_value: v }))} disabled={busy} placeholder="Opcional" />
                                        </label>
                                        <div className="own-edit__actions">
                                            <button type="button" className="own-btn own-btn--ghost" onClick={() => setEditId(null)} disabled={busy}>Cancelar</button>
                                            <button type="button" className="own-btn own-btn--primary" onClick={() => saveEdit(o)} disabled={busy}>Guardar</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="own-item__meta">
                                        <span className="own-item__pct">{o.percent}%</span>
                                        <span className="own-item__sep">·</span>
                                        <span>{o.purchase_date ? `Comprado ${formatDateOnly(o.purchase_date)}` : 'Sin fecha'}</span>
                                        <span className="own-item__sep">·</span>
                                        <span>{fmtMoney(o.purchase_value)}</span>
                                        {o.stake_value != null && <span className="own-item__stake">Participación {fmtMoney(o.stake_value)}</span>}
                                    </div>
                                )}
                            </li>
                        ))}
                        {!owners.length && <li className="own-item own-item--none">Sin copropietarios (100% de la planta).</li>}
                    </ul>
                )}

                <div className="own-add">
                    <span className="own-add__title">Agregar copropietario</span>
                    {planta <= 0 ? (
                        <p className="own-add__full">Ya está repartido el 100%. Editá o quitá un copropietario para liberar %.</p>
                    ) : !client ? (
                        <ClientFarmSearch scope="clients" placeholder="Buscar cliente…" onSelect={(c) => pick(c)} />
                    ) : (
                        <div className="own-add__form">
                            <span className="own-add__client"><IconCheckCircle /> {client.name}
                                <button type="button" className="own-add__clear" onClick={resetAdd} aria-label="Cambiar cliente">×</button>
                            </span>
                            <div className="own-edit">
                                <label className="own-field own-field--pct">
                                    <span className="own-field__label">% (máx {planta})</span>
                                    <input type="number" min="1" max={planta} value={pct} onChange={(e) => setPct(e.target.value)} disabled={busy} />
                                </label>
                                <label className="own-field">
                                    <span className="own-field__label">Fecha de compra</span>
                                    <input type="date" max={today()} value={pdate} onChange={(e) => setPdate(e.target.value)} disabled={busy} />
                                </label>
                                <label className="own-field">
                                    <span className="own-field__label">Valor de compra</span>
                                    <MoneyInput value={pvalue} onChange={setPvalue} disabled={busy} placeholder="Opcional" />
                                </label>
                                <div className="own-edit__actions">
                                    <button type="button" className="own-btn own-btn--primary" onClick={addOwner} disabled={busy || !pct}>Agregar</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <footer className="own-modal__foot">
                    <button type="button" className="own-btn own-btn--ghost" onClick={onClose}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
